import useSidebarState from '../hooks/useSidebarState';
import { uiText } from '../utils/uiTranslation';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import Sidebar from './Sidebar';
import { useSearchParams } from 'react-router-dom';
import './ContractsDocs.css';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';
import { ref as dbRef, get, onValue, push, set, remove } from 'firebase/database';
import { ref as stRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import FeatureGate from './Trial/FeatureGate';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import UpgradeModal from './UpgradeModal';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

// ─── PDF generation — pure jsPDF text API, no canvas ─────────────────────────
// Parses the editor's HTML with DOMParser, walks the DOM tree, and renders each
// element via jsPDF's text/line primitives. No html2canvas → no blank-page bug.
const generatePDFBlob = async (htmlContent) => {
  const { default: jsPDF } = await import('jspdf');

  // A4 in points (1pt = 1/72 inch)
  const PW = 595.28, PH = 841.89;
  const ML = 72, MR = 72, MT = 72, MB = 80;
  const CW = PW - ML - MR; // usable content width ~451pt

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  let y = MT;

  const newPage = () => { pdf.addPage(); y = MT; };
  const need = (h) => { if (y + h > PH - MB) newPage(); };

  // Render a plain-text block with word-wrap and page-break awareness
  const block = ({ text, size = 12, style = 'normal', align = 'left', indent = 0, before = 0, after = 8, rgb = [17, 17, 17] }) => {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (!clean) return;
    y += before;
    pdf.setFontSize(size);
    pdf.setFont('times', style);
    pdf.setTextColor(...rgb);
    const lines = pdf.splitTextToSize(clean, CW - indent);
    const lh = size * 1.45;
    lines.forEach(line => {
      need(lh);
      pdf.text(line, align === 'center' ? PW / 2 : ML + indent, y, { align });
      y += lh;
    });
    y += after;
    pdf.setTextColor(17, 17, 17);
  };

  // Render inline content (handles bold/italic runs) with word-wrap
  const inline = (node, size = 12, indent = 0, before = 6, after = 6) => {
    // Collect styled word tokens from the subtree
    const tokens = [];
    const collect = (n, bold, italic) => {
      if (n.nodeType === Node.TEXT_NODE) {
        n.textContent.split(/(\s+)/).forEach(w => w && tokens.push({ w, bold, italic }));
      } else {
        const t = n.nodeName.toLowerCase();
        const b = bold || t === 'strong' || t === 'b';
        const i = italic || t === 'em' || t === 'i';
        n.childNodes.forEach(c => collect(c, b, i));
      }
    };
    collect(node, false, false);
    if (!tokens.length) return;

    y += before;
    let xOff = 0;   // offset from ML+indent for the current line
    const lh = size * 1.45;

    const flushAndAdvance = () => { y += lh; xOff = 0; };
    const getStyle = (b, i) => b && i ? 'bolditalic' : b ? 'bold' : i ? 'italic' : 'normal';

    for (const { w, bold, italic } of tokens) {
      pdf.setFont('times', getStyle(bold, italic));
      pdf.setFontSize(size);
      const ww = pdf.getTextWidth(w);
      const isSpace = /^\s+$/.test(w);

      if (!isSpace && xOff + ww > CW - indent) {
        need(lh);
        flushAndAdvance();
      }
      need(lh);
      if (!isSpace || xOff > 0) {
        pdf.text(w, ML + indent + xOff, y);
        xOff += ww;
      }
    }
    // advance past last line
    if (xOff > 0) y += lh;
    y += after;
  };

  // Walk DOM and dispatch each node
  const walk = (node) => {
    if (!node) return;
    const t = node.nodeName.toLowerCase();

    if (node.nodeType === Node.TEXT_NODE) {
      const txt = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (txt) block({ text: txt, before: 0, after: 0 });
      return;
    }

    switch (t) {
      case 'h1':
        block({ text: node.textContent, size: 22, style: 'bold', align: 'center', before: 0, after: 16 });
        break;
      case 'h2':
        need(50);
        block({ text: node.textContent, size: 17, style: 'bold', before: 20, after: 3 });
        pdf.setDrawColor(180, 180, 180);
        pdf.line(ML, y, PW - MR, y);
        y += 8;
        break;
      case 'h3':
        block({ text: node.textContent, size: 14, style: 'bold', before: 14, after: 5 });
        break;
      case 'h4': case 'h5': case 'h6':
        block({ text: node.textContent, size: 12, style: 'bold', before: 10, after: 4 });
        break;
      case 'p':
        inline(node, 12, 0, 6, 6);
        break;
      case 'ul': case 'ol': {
        y += 4;
        let idx = 1;
        for (const li of node.querySelectorAll(':scope > li')) {
          const bullet = t === 'ol' ? `${idx++}.` : '•';
          const lh = 12 * 1.45;
          // Set font BEFORE measuring so splitTextToSize uses the correct metrics
          pdf.setFont('times', 'normal'); pdf.setFontSize(12);
          const liText = (li.textContent || '').replace(/\s+/g, ' ').trim();
          const lines = pdf.splitTextToSize(liText, CW - 26);
          // Check space line-by-line so a long item never causes premature blank space
          lines.forEach((line, li2) => {
            need(lh);
            if (li2 === 0) pdf.text(bullet, ML + 4, y);
            pdf.text(line, ML + 22, y);
            y += lh;
          });
          y += 3;
        }
        y += 4;
        break;
      }
      case 'hr':
        need(20); y += 8;
        pdf.setDrawColor(180, 180, 180);
        pdf.line(ML, y, PW - MR, y);
        y += 12;
        break;
      case 'blockquote':
        block({ text: node.textContent, size: 12, style: 'italic', indent: 18, before: 6, after: 6, rgb: [100, 100, 100] });
        break;
      case 'table': {
        const rows = Array.from(node.querySelectorAll('tr'));
        if (!rows.length) break;
        const maxCols = Math.max(...rows.map(r => r.querySelectorAll('td,th').length));
        if (!maxCols) break;
        const cw = CW / maxCols;
        const rowH = 22;
        for (const row of rows) {
          const cells = Array.from(row.querySelectorAll('td,th'));
          const isHdr = cells.some(c => c.nodeName.toLowerCase() === 'th');
          need(rowH);
          cells.forEach((cell, ci) => {
            const cx = ML + ci * cw, cy = y - 14;
            if (isHdr) { pdf.setFillColor(242, 242, 242); pdf.rect(cx, cy, cw, rowH, 'F'); }
            pdf.setDrawColor(180, 180, 180); pdf.rect(cx, cy, cw, rowH);
            pdf.setFont('times', isHdr ? 'bold' : 'normal'); pdf.setFontSize(10);
            const ct = pdf.splitTextToSize((cell.textContent || '').trim(), cw - 8);
            pdf.text(ct[0] || '', cx + 4, y);
          });
          y += rowH;
        }
        y += 6;
        break;
      }
      case 'br': y += 10; break;
      default:
        node.childNodes.forEach(walk);
    }
  };

  const parser = new DOMParser();
  const dom = parser.parseFromString(`<body>${htmlContent}</body>`, 'text/html');
  dom.body.childNodes.forEach(walk);

  // Centred page numbers on every page
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9); pdf.setTextColor(136, 136, 136); pdf.setFont('times', 'normal');
    pdf.text(`${i} / ${total}`, PW / 2, PH - 30, { align: 'center' });
  }

  return pdf.output('blob');
};
// ─────────────────────────────────────────────────────────────────────────────

// Derive a stable DOM id from heading text for scroll targeting
const headingId = (children) => {
  const text = Array.isArray(children)
    ? children.map(c => (typeof c === 'string' ? c : '')).join('')
    : typeof children === 'string' ? children : '';
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'section';
};

const normalizeHeadingText = (value = '') => (
  String(value)
    .replace(/^#+\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
);

const headingLevelFromTagName = (tagName = '') => {
  const match = String(tagName).toLowerCase().match(/^h([1-6])$/);
  return match ? Number(match[1]) : 0;
};

const retagElement = (element, tagName) => {
  if (!element || element.tagName.toLowerCase() === tagName.toLowerCase()) return element;
  const replacement = document.createElement(tagName.toLowerCase());
  Array.from(element.attributes).forEach(attr => replacement.setAttribute(attr.name, attr.value));
  while (element.firstChild) replacement.appendChild(element.firstChild);
  element.replaceWith(replacement);
  return replacement;
};

const isExplicitRedraftRequest = (value = '') => (
  /\b(redraft|rewrite|regenerate|recreate)\b.*\b(whole|entire|full|document|contract)\b/i.test(value)
  || /\b(whole|entire|full)\b.*\b(redraft|rewrite|regenerate|recreate)\b/i.test(value)
);

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

const waitForScrollFocus = async () => {
  await nextFrame();
  await new Promise(resolve => setTimeout(resolve, 220));
};

// Replace one section of a Markdown string with new content.
// Matches by heading text; replaces from that heading to the next heading
// at the same or higher level.
const applyMarkdownPatch = (content, targetHeading, replacement) => {
  const lines = content.split('\n');
  const levelMatch = replacement.trimStart().match(/^(#+)\s/);
  const headingLevel = levelMatch ? levelMatch[1].length : 2;
  const prefix = '#'.repeat(headingLevel);
  const escaped = targetHeading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const headingRe = new RegExp(`^${prefix}\\s+${escaped}\\s*$`, 'i');

  let startIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (headingRe.test(lines[i].trim())) { startIdx = i; break; }
  }
  if (startIdx === -1) return content + '\n\n' + replacement;

  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i++) {
    const m = lines[i].match(/^(#+)\s/);
    if (m && m[1].length <= headingLevel) { endIdx = i; break; }
  }

  return [
    ...lines.slice(0, startIdx),
    ...replacement.split('\n'),
    '',
    ...lines.slice(endIdx),
  ].join('\n');
};

const CONTRACTS_MODEL = 'gpt-5.4-mini';

const getClarifyOptionValue = (option) => (
  option && typeof option === 'object' ? option.value : option
);

const getClarifyOptionLabel = (option) => (
  option && typeof option === 'object' ? option.label : option
);

const isDuplicateJurisdictionQuestion = (question = {}) => (
  /\b(country|jurisdiction|governing law|applicable law|venue|choice of law)\b/i.test(question.question || '')
);

const containsArabicText = (value = '') => /[\u0600-\u06FF]/.test(value);

const normalizeDocumentLanguage = (value = '') => {
  const language = String(value || '').trim().toLowerCase();
  if (['ar', 'ara', 'arabic', 'العربية', 'عربي'].includes(language)) return 'ar';
  if (['en', 'eng', 'english'].includes(language)) return 'en';
  return '';
};

const normalizeContractMarkdownLists = (markdown = '') => {
  const lines = String(markdown || '').split('\n');
  const out = [];
  let previousWasList = false;
  lines.forEach((line, index) => {
    const isList = /^\s*(?:\d+[.)]|[-*•])\s+/.test(line);
    if (isList && out.length && out[out.length - 1].trim() && !previousWasList) out.push('');
    out.push(line);
    const next = lines[index + 1] || '';
    if (isList && next.trim() && !/^\s*(?:\d+[.)]|[-*•])\s+/.test(next)) out.push('');
    previousWasList = isList;
  });
  return out.join('\n');
};

const stripLeadingListMarkerHTML = (html = '') => (
  String(html).replace(/^\s*(?:\d+[.)]|[-*•])\s+/, '')
);

const convertParagraphListRuns = (root) => {
  const paragraphs = Array.from(root.querySelectorAll('p'));
  paragraphs.forEach((paragraph) => {
    if (!paragraph.parentNode || paragraph.dataset.listConverted === 'true') return;
    const text = paragraph.textContent || '';
    const marker = text.match(/^\s*(?:(\d+)[.)]|([-*•]))\s+/);
    if (!marker) return;

    const parent = paragraph.parentNode;
    const listTag = marker[1] ? 'ol' : 'ul';
    const list = document.createElement(listTag);
    if (marker[1]) list.setAttribute('start', marker[1]);

    let current = paragraph;
    while (current && current.nodeType === Node.ELEMENT_NODE && current.tagName.toLowerCase() === 'p') {
      const currentText = current.textContent || '';
      const currentMarker = currentText.match(/^\s*(?:(\d+)[.)]|([-*•]))\s+/);
      const currentTag = currentMarker?.[1] ? 'ol' : currentMarker ? 'ul' : '';
      if (currentTag !== listTag) break;

      const li = document.createElement('li');
      li.innerHTML = stripLeadingListMarkerHTML(current.innerHTML);
      list.appendChild(li);
      current.dataset.listConverted = 'true';
      const next = current.nextElementSibling;
      current.remove();
      current = next;
    }

    if (list.children.length) {
      parent.insertBefore(list, current || null);
    }
  });
};

const isolateLTRTokens = (root) => {
  const tokenPattern = /(\[[A-Za-z0-9_./:+\-\s]+\]|[A-Z][A-Z0-9_./:+-]{2,}|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|(?:https?:\/\/)?[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\/[^\s\u0600-\u06FF]*)?)/g;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || parent.closest('.doc-ltr-token')) return NodeFilter.FILTER_REJECT;
      tokenPattern.lastIndex = 0;
      if (!tokenPattern.test(node.nodeValue || '')) return NodeFilter.FILTER_REJECT;
      tokenPattern.lastIndex = 0;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => {
    const text = node.nodeValue || '';
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    text.replace(tokenPattern, (match, _token, offset) => {
      if (offset > lastIndex) fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
      const bdi = document.createElement('bdi');
      bdi.className = 'doc-ltr-token';
      bdi.dir = 'ltr';
      bdi.textContent = match;
      fragment.appendChild(bdi);
      lastIndex = offset + match.length;
      return match;
    });
    if (lastIndex < text.length) fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    node.parentNode?.replaceChild(fragment, node);
  });
};

const enhanceLegalDocHTML = (html = '', { rtl = false } = {}) => {
  if (typeof document === 'undefined') return html;
  const temp = document.createElement('div');
  temp.innerHTML = html;
  if (rtl) {
    convertParagraphListRuns(temp);
    isolateLTRTokens(temp);
    temp.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6').forEach(node => {
      node.setAttribute('dir', 'rtl');
    });
  }
  return temp.innerHTML;
};

const renderContractMarkdownHTML = (markdown = '', { rtl = false } = {}) => (
  enhanceLegalDocHTML(marked.parse(normalizeContractMarkdownLists(markdown)), { rtl })
);

const getClarifyCopy = (lang = 'en') => {
  if (lang === 'ar') {
    return {
      header: 'بعض التفاصيل لتخصيص المستند:',
      generate: 'إنشاء المستند',
      jurisdictionQuestion: 'ما الدولة أو الولاية القضائية التي يجب تكييف هذا المستند لها؟',
      jurisdictionPlaceholder: 'مثال: لبنان، الإمارات، ألمانيا، أونتاريو، DIFC',
      languageQuestion: 'هل تريد إنشاء المستند بالإنجليزية أم بالعربية؟',
      languageEnglish: 'الإنجليزية',
      languageArabic: 'العربية',
    };
  }
  return null;
};

const ContractsDocs = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
  const { currentUser } = useAuth();
  const { canUseAIContracts } = useFeatureAccess();
  const { t } = useTranslation(['contracts', 'common']);
  const { language, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();

  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [companyUID, setCompanyUID] = useState('');

  // Library state
  const [docs, setDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadTagInput, setUploadTagInput] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadTagDropdown, setShowUploadTagDropdown] = useState(false);

  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveTags, setSaveTags] = useState([]);
  const [saveTagInput, setSaveTagInput] = useState('');
  const [showSaveTagDropdown, setShowSaveTagDropdown] = useState(false);
  const [sessionTags, setSessionTags] = useState([]);
  const [saving, setSaving] = useState(false);

  // AI drafting
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPromptPulse, setAiPromptPulse] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [messages, setMessages] = useState([]);
  const [editText, setEditText] = useState('');
  const [selectedExcerpt, setSelectedExcerpt] = useState('');
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);

  // Clarification step
  const [clarifyStep, setClarifyStep] = useState('idle'); // 'idle' | 'loading' | 'ready'
  const [clarifyQuestions, setClarifyQuestions] = useState([]);
  const [clarifyAnswers, setClarifyAnswers] = useState({});
  const [clarifyLanguage, setClarifyLanguage] = useState('');
  const [documentJurisdiction, setDocumentJurisdiction] = useState('');
  const [documentLanguage, setDocumentLanguage] = useState('');

  // Document content — plain Markdown string (source of truth for AI operations)
  const [masterContent, setMasterContent] = useState('');

  // Chat pane AI response streaming
  const [chatStreamText, setChatStreamText] = useState('');
  const [isChatResponding, setIsChatResponding] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);

  // Toolbar active-format state (bold / italic / underline)
  const [formatState, setFormatState] = useState({ bold: false, italic: false, underline: false });

  const streamAbortRef = useRef(null);
  const editorScrollRef = useRef(null);
  const editorContentRef = useRef(null); // the contentEditable document body
  const chatMessagesRef = useRef(null);
  const aiPromptRef = useRef(null);
  const selectedRangeRef = useRef(null);
  const pendingEditorHTMLRef = useRef('');

  const interfaceLanguage = language || 'en';
  const clarifyCopy = useMemo(() => getClarifyCopy(clarifyLanguage) || {}, [clarifyLanguage]);

  const mandatoryClarifyQuestions = useMemo(() => ([
    {
      id: 'jurisdiction',
      type: 'text',
      required: true,
      question: clarifyCopy.jurisdictionQuestion || t('ai.clarify.jurisdictionQuestion', 'Which country or jurisdiction should this document be adapted to?'),
      placeholder: clarifyCopy.jurisdictionPlaceholder || t('ai.clarify.jurisdictionPlaceholder', 'Example: Lebanon, UAE, Germany, Ontario, DIFC'),
    },
    {
      id: 'document_language',
      type: 'choice',
      required: true,
      question: clarifyCopy.languageQuestion || t('ai.clarify.languageQuestion', 'Generate this document in English or Arabic?'),
      options: [
        { label: clarifyCopy.languageEnglish || t('ai.clarify.languageEnglish', 'English'), value: 'en' },
        { label: clarifyCopy.languageArabic || t('ai.clarify.languageArabic', 'Arabic'), value: 'ar' },
      ],
    },
  ]), [clarifyCopy, t]);

  const selectedDocumentJurisdiction = documentJurisdiction.trim();
  const selectedDocumentLanguage = documentLanguage || '';
  const documentIsRTL = selectedDocumentLanguage === 'ar' || (!selectedDocumentLanguage && containsArabicText(masterContent));
  const documentDirection = documentIsRTL ? 'rtl' : 'ltr';
  const documentDirectionClass = documentIsRTL ? 'doc-rtl' : 'doc-ltr';
  const requiredClarifyAnswersComplete = useMemo(() => (
    mandatoryClarifyQuestions.every(q => {
      const value = String(clarifyAnswers[q.id] || '').trim();
      return !q.required || Boolean(value);
    })
  ), [clarifyAnswers, mandatoryClarifyQuestions]);

  // ReactMarkdown heading components: add id= so scrollToSection can find them
  const mdComponents = useMemo(() => ({
    h1: ({ children, ...p }) => <h1 id={headingId(children)} {...p}>{children}</h1>,
    h2: ({ children, ...p }) => <h2 id={headingId(children)} {...p}>{children}</h2>,
    h3: ({ children, ...p }) => <h3 id={headingId(children)} {...p}>{children}</h3>,
    h4: ({ children, ...p }) => <h4 id={headingId(children)} {...p}>{children}</h4>,
  }), []);

  // Scroll the editor to a section by its heading text
  const scrollToSection = useCallback((heading) => {
    const normalized = normalizeHeadingText(heading);
    const headings = Array.from(editorScrollRef.current?.querySelectorAll('h1,h2,h3,h4,h5,h6') || []);
    const el = headings.find(h => normalizeHeadingText(h.textContent) === normalized)
      || headings.find(h => normalizeHeadingText(h.textContent).includes(normalized) || normalized.includes(normalizeHeadingText(h.textContent)));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const revealStreamChunk = useCallback(async (chunk, append) => {
    append(chunk);
  }, []);

  const scrollChatToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    });
  }, []);

  const updateSelectedExcerpt = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorContentRef.current) {
      setSelectedExcerpt('');
      selectedRangeRef.current = null;
      return;
    }

    const range = selection.getRangeAt(0);
    if (selection.isCollapsed || !editorContentRef.current.contains(range.commonAncestorContainer)) {
      setSelectedExcerpt('');
      selectedRangeRef.current = null;
      return;
    }

    const text = selection.toString().replace(/\s+/g, ' ').trim();
    if (!text) {
      setSelectedExcerpt('');
      selectedRangeRef.current = null;
      return;
    }

    selectedRangeRef.current = range.cloneRange();
    setSelectedExcerpt(text.slice(0, 1200));
  }, []);

  const selectedContextLabel = useMemo(() => {
    if (!selectedExcerpt) return '';
    const words = selectedExcerpt.split(/\s+/).filter(Boolean);
    if (words.length <= 6) return selectedExcerpt;
    return `${words.slice(0, 3).join(' ')}...${words.slice(-3).join(' ')}`;
  }, [selectedExcerpt]);

  const clearSelectedContext = useCallback(() => {
    setSelectedExcerpt('');
    selectedRangeRef.current = null;
    window.getSelection()?.removeAllRanges();
  }, []);

  const applySelectedReplacement = useCallback(async (replacement) => {
    const range = selectedRangeRef.current;
    if (!range || !editorContentRef.current || !replacement) return null;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    const startNode = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : range.startContainer;
    startNode?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    await waitForScrollFocus();

    range.deleteContents();
    const replacementNode = document.createTextNode('');
    range.insertNode(replacementNode);
    selection.removeAllRanges();
    const parts = String(replacement).match(/(\s+|[^\s]+)/g) || [replacement];
    for (const part of parts) {
      replacementNode.nodeValue += part;
      await nextFrame();
    }

    clearSelectedContext();
    return replacementNode.parentElement || startNode;
  }, [clearSelectedContext]);

  const findSectionHeadingElement = useCallback((operation = {}) => {
    const headings = Array.from(editorContentRef.current?.querySelectorAll('h1,h2,h3,h4,h5,h6') || []);
    if (!headings.length) return null;

    const candidates = [
      operation.section_heading,
      operation.heading,
      operation.normalized_heading,
      operation.target_heading,
      operation.target,
      operation.section_number ? `section ${operation.section_number}` : '',
    ].filter(Boolean).map(normalizeHeadingText);

    return headings.find(heading => {
      const text = normalizeHeadingText(heading.textContent);
      const number = (heading.textContent || '').match(/^(?:section\s*)?(\d+(?:\.\d+)*)[\).:\-\s]+/i)?.[1];
      return candidates.some(candidate => (
        text === candidate
        || text.includes(candidate)
        || candidate.includes(text)
        || (operation.section_number && number === String(operation.section_number))
      ));
    }) || null;
  }, []);

  const applySectionReplacementToEditor = useCallback(async (targetOperation, replacement) => {
    if (!editorContentRef.current || !replacement) return null;

    const temp = document.createElement('div');
    temp.innerHTML = renderContractMarkdownHTML(replacement, { rtl: selectedDocumentLanguage === 'ar' || containsArabicText(replacement) });
    const replacementHeading = temp.querySelector('h1,h2,h3,h4,h5,h6');
    const operation = typeof targetOperation === 'object'
      ? { ...targetOperation }
      : { section_heading: targetOperation };
    if (!operation.section_heading && !operation.heading && !operation.normalized_heading) {
      operation.section_heading = replacementHeading?.textContent || '';
    }

    const target = findSectionHeadingElement(operation);
    if (!target) return null;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await waitForScrollFocus();

    const targetLevel = headingLevelFromTagName(target.tagName);
    if (replacementHeading) retagElement(replacementHeading, target.tagName);

    let cursor = target.nextSibling;
    const toRemove = [target];
    while (cursor) {
      if (cursor.nodeType === Node.ELEMENT_NODE) {
        const cursorLevel = headingLevelFromTagName(cursor.tagName);
        if (cursorLevel && cursorLevel <= targetLevel) break;
      }
      toRemove.push(cursor);
      cursor = cursor.nextSibling;
    }

    const parent = target.parentNode;
    const insertBefore = cursor;
    const insertedNodes = Array.from(temp.childNodes).map(node => node.cloneNode(true));
    toRemove.forEach(node => node.parentNode?.removeChild(node));
    let scrollTarget = null;
    for (const node of insertedNodes) {
      parent.insertBefore(node, insertBefore);
      if (!scrollTarget && node.nodeType === Node.ELEMENT_NODE) scrollTarget = node;
      await nextFrame();
    }

    scrollTarget?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    return scrollTarget || parent;
  }, [findSectionHeadingElement, selectedDocumentLanguage]);

  const buildEditorSectionIndex = useCallback(() => {
    const headings = Array.from(editorContentRef.current?.querySelectorAll('h1,h2,h3,h4,h5,h6') || []);
    return headings.map((heading, index) => {
      const text = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      const numberMatch = text.match(/^(?:section\s*)?(\d+(?:\.\d+)*)[\).:\-\s]+/i);
      const level = headingLevelFromTagName(heading.tagName);
      const bodyNodes = [];
      let cursor = heading.nextSibling;
      while (cursor) {
        if (cursor.nodeType === Node.ELEMENT_NODE) {
          const cursorLevel = headingLevelFromTagName(cursor.tagName);
          if (cursorLevel && cursorLevel <= level) break;
        }
        bodyNodes.push(cursor);
        cursor = cursor.nextSibling;
      }
      const bodyHTML = bodyNodes.map(node => node.outerHTML || node.textContent || '').join('');
      const bodyText = bodyNodes.map(node => node.textContent || '').join('\n').replace(/\s+/g, ' ').trim();
      return {
        section_id: `section-${index + 1}`,
        index: index + 1,
        heading: text,
        normalized_heading: normalizeHeadingText(text),
        visible_number: numberMatch ? numberMatch[1] : String(index + 1),
        level: headingLevelFromTagName(heading.tagName),
        body_excerpt: bodyText.slice(0, 900),
        html_excerpt: bodyHTML.slice(0, 2400),
      };
    });
  }, [selectedDocumentLanguage]);

  const syncEditorState = useCallback(() => {
    if (!editorContentRef.current) return;
    const updatedHTML = editorContentRef.current.innerHTML;
    pendingEditorHTMLRef.current = updatedHTML;
    setMasterContent(editorContentRef.current.innerText || '');
  }, []);

  const applyTextReplacementAll = useCallback(async (operation = {}) => {
    const root = editorContentRef.current;
    const find = operation.find ?? operation.from;
    const replace = operation.replace ?? operation.to ?? '';
    if (!root || !find) return null;

    const flags = operation.case_sensitive === false ? 'gi' : 'g';
    const escaped = String(find).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(escaped, flags);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let firstChanged = null;
    let node = walker.nextNode();
    while (node) {
      if (pattern.test(node.nodeValue)) {
        pattern.lastIndex = 0;
        if (!firstChanged) {
          firstChanged = node.parentElement;
          firstChanged?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          await waitForScrollFocus();
        }
        node.nodeValue = node.nodeValue.replace(pattern, replace);
        await nextFrame();
      }
      pattern.lastIndex = 0;
      node = walker.nextNode();
    }
    return firstChanged;
  }, []);

  const getSectionBoundary = useCallback((heading) => {
    if (!heading) return null;
    const level = headingLevelFromTagName(heading.tagName);
    let cursor = heading.nextSibling;
    const nodes = [heading];
    while (cursor) {
      if (cursor.nodeType === Node.ELEMENT_NODE) {
        const cursorLevel = headingLevelFromTagName(cursor.tagName);
        if (cursorLevel && cursorLevel <= level) break;
      }
      nodes.push(cursor);
      cursor = cursor.nextSibling;
    }
    return { heading, nodes, after: cursor };
  }, []);

  const insertSectionContent = useCallback(async (operation = {}, position = 'after') => {
    const target = findSectionHeadingElement(operation);
    const content = operation.content || operation.text || operation.replacement || '';
    if (!target || !content) return null;

    const boundary = getSectionBoundary(target);
    if (!boundary) return null;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await waitForScrollFocus();

    const temp = document.createElement('div');
    temp.innerHTML = renderContractMarkdownHTML(content, { rtl: selectedDocumentLanguage === 'ar' || containsArabicText(content) });
    const nodes = Array.from(temp.childNodes).map(node => node.cloneNode(true));
    const parent = target.parentNode;
    const anchor = position === 'before' ? target : boundary.after;
    let firstInserted = null;
    for (const node of nodes) {
      parent.insertBefore(node, anchor);
      if (!firstInserted && node.nodeType === Node.ELEMENT_NODE) firstInserted = node;
      await nextFrame();
    }
    return firstInserted || target;
  }, [findSectionHeadingElement, getSectionBoundary, selectedDocumentLanguage]);

  const deleteSectionFromEditor = useCallback(async (operation = {}) => {
    const target = findSectionHeadingElement(operation);
    const boundary = getSectionBoundary(target);
    if (!boundary) return null;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await waitForScrollFocus();
    const next = boundary.after?.nodeType === Node.ELEMENT_NODE ? boundary.after : null;
    boundary.nodes.forEach(node => node.parentNode?.removeChild(node));
    return next || editorContentRef.current;
  }, [findSectionHeadingElement, getSectionBoundary]);

  const applyEditOperation = useCallback(async (operation = {}, promptText = '') => {
    const type = operation.type;
    if (!type) return null;

    if (type === 'replace_text_all') {
      return await applyTextReplacementAll(operation);
    }
    if (type === 'replace_selection') {
      return await applySelectedReplacement(operation.replacement || operation.text || operation.selected_replacement);
    }
    if (type === 'replace_section') {
      return await applySectionReplacementToEditor(operation, operation.replacement);
    }
    if (type === 'replace_sections') {
      let firstTarget = null;
      for (const sectionOp of (operation.sections || operation.replacements || [])) {
        const applied = await applySectionReplacementToEditor(sectionOp, sectionOp.replacement);
        if (applied && !firstTarget) firstTarget = applied;
      }
      return firstTarget;
    }
    if (type === 'insert_before_section') {
      return await insertSectionContent(operation, 'before');
    }
    if (type === 'insert_after_section') {
      return await insertSectionContent(operation, 'after');
    }
    if (type === 'delete_section') {
      return await deleteSectionFromEditor(operation);
    }
    if (type === 'redraft_document') {
      if (!isExplicitRedraftRequest(promptText)) return null;
      const documentText = operation.document || operation.replacement || '';
      if (!documentText || !editorContentRef.current) return null;
      editorContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      await waitForScrollFocus();
      const temp = document.createElement('div');
      temp.innerHTML = renderContractMarkdownHTML(documentText, { rtl: selectedDocumentLanguage === 'ar' || containsArabicText(documentText) });
      editorContentRef.current.innerHTML = '';
      const nodes = Array.from(temp.childNodes).map(node => node.cloneNode(true));
      for (const node of nodes) {
        editorContentRef.current.appendChild(node);
        await nextFrame();
      }
      return editorContentRef.current.querySelector('h1,h2,h3,h4,h5,h6') || editorContentRef.current;
    }
    return null;
  }, [
    applySectionReplacementToEditor,
    applySelectedReplacement,
    applyTextReplacementAll,
    deleteSectionFromEditor,
    insertSectionContent,
    selectedDocumentLanguage,
  ]);

  // Read HTML from the live editor (includes any manual edits); fall back to parsed markdown
  const getHTMLContent = useCallback(() => {
    const liveHTML = editorContentRef.current?.innerHTML?.trim();
    if (liveHTML) {
      return enhanceLegalDocHTML(liveHTML, { rtl: documentIsRTL || containsArabicText(liveHTML) });
    }
    if (masterContent) return renderContractMarkdownHTML(masterContent, { rtl: documentIsRTL || containsArabicText(masterContent) });
    return '';
  }, [documentIsRTL, masterContent]);

  const createPDFBlob = useCallback(async (htmlContent, title) => {
    const isArabicDocument = selectedDocumentLanguage === 'ar' || containsArabicText(htmlContent);
    if (!isArabicDocument) return generatePDFBlob(htmlContent);

    const pdfHTML = `<div class="legal-doc doc-rtl" dir="rtl" lang="ar">${htmlContent}</div>`;
    const resp = await fetch(`${BACKEND_URL}/contracts/ai/render-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        html: pdfHTML,
        title,
        page_size: 'A4',
        language: 'ar',
        direction: 'rtl',
      }),
    });

    if (!resp.ok) {
      const detail = await resp.text().catch(() => '');
      throw new Error(`Backend Arabic PDF render failed (${resp.status}): ${detail}`);
    }
    return resp.blob();
  }, [BACKEND_URL, selectedDocumentLanguage]);

  // Whenever AI finishes generating or patches the doc, push HTML into the contentEditable editor.
  // We deliberately skip this while isGenerating (streaming view is shown instead).
  useEffect(() => {
    if (!isGenerating && masterContent && editorContentRef.current) {
      if (pendingEditorHTMLRef.current) {
        const pendingHTML = pendingEditorHTMLRef.current;
        editorContentRef.current.innerHTML = enhanceLegalDocHTML(pendingHTML, {
          rtl: documentIsRTL || containsArabicText(pendingHTML),
        });
        pendingEditorHTMLRef.current = '';
      } else {
        editorContentRef.current.innerHTML = renderContractMarkdownHTML(masterContent, { rtl: documentIsRTL || containsArabicText(masterContent) });
      }
    }
  }, [documentIsRTL, isGenerating, masterContent]);

  // Toolbar helpers — operate on the focused contentEditable editor
  const execFormat = useCallback((command) => {
    document.execCommand(command, false, null);
    editorContentRef.current?.focus();
  }, []);

  // Stream an AI explanation into the chat pane after doc generation/edit.
  // docExcerpt: first ~600 chars of the generated document for context.
  const streamChatResponse = useCallback(async (prompt, mode, docExcerpt, context = {}) => {
    const responseLanguage = context.language || selectedDocumentLanguage || interfaceLanguage;
    const responseJurisdiction = context.country || selectedDocumentJurisdiction || '';
    setIsChatResponding(true);
    setChatStreamText('');
    let full = '';
    try {
      const resp = await fetch(`${BACKEND_URL}/contracts/ai/chat-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          mode,
          language: responseLanguage,
          country: responseJurisdiction,
          model: CONTRACTS_MODEL,
          document_excerpt: docExcerpt,
        }),
      });
      if (!resp.ok || !resp.body) return;
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          await revealStreamChunk(chunk, part => {
            full += part;
            setChatStreamText(full);
          });
          // Auto-scroll chat to bottom while streaming
          if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
          }
        }
      }
    } catch (e) {
      console.warn('Chat response stream error:', e);
      full = '**Important:** Please have a qualified legal professional review this document before use — AI-generated legal content may not reflect the latest laws or jurisdiction-specific requirements.';
      setChatStreamText(full);
    } finally {
      // Commit the complete message and clear the streaming buffer
      if (full) {
        setMessages(prev => [...prev, { id: Date.now(), role: 'ai', text: full }]);
      }
      setChatStreamText('');
      setIsChatResponding(false);
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }
  }, [BACKEND_URL, revealStreamChunk, interfaceLanguage, selectedDocumentLanguage, selectedDocumentJurisdiction]);

  // Track bold/italic/underline state of the cursor so toolbar buttons highlight
  useEffect(() => {
    const update = () => {
      if (!editorContentRef.current?.contains(document.activeElement)) return;
      setFormatState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    };
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, []);

  // Scroll chat to bottom whenever clarify step changes (questions just appeared)
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [clarifyStep]);

  useEffect(() => {
    scrollChatToBottom();
  }, [messages, chatStreamText, isApplyingEdit, isChatResponding, scrollChatToBottom]);

  const openAIDocument = useCallback(async (doc) => {
    try {
      let content = '';
      if (doc.HTMLContract) {
        const temp = document.createElement('div');
        temp.innerHTML = doc.HTMLContract;
        content = temp.innerText || '';
      }
      if (!content) content = doc.markdownContent || '';
      if (!content && doc.htmlPath) {
        content = await fetch(await getDownloadURL(stRef(storage, doc.htmlPath))).then(r => r.text());
      }

      setDrafting(true);
      setMessages([{
        id: Date.now(),
        role: 'ai',
        text: t('ai.status.loadedEditable', 'Loaded this AI-generated document back into the editor. Highlight contract text to focus it or send it to the AI for targeted edits.'),
      }]);
      setStatusText(t('ai.status.loaded', 'Loaded saved document'));
      if (doc.HTMLContract) pendingEditorHTMLRef.current = doc.HTMLContract;
      setMasterContent(content);
      setActiveDoc({ id: doc.id, title: doc.title || '', tags: Array.isArray(doc.tags) ? doc.tags : [], type: doc.type || 'ai' });
      setDocumentJurisdiction(doc.country || '');
      setDocumentLanguage(normalizeDocumentLanguage(doc.language) || (containsArabicText(content || doc.HTMLContract || '') ? 'ar' : 'en'));
      setSelectedExcerpt('');
      selectedRangeRef.current = null;
      setClarifyStep('idle');
      setClarifyQuestions([]);
      setClarifyAnswers({});
      setClarifyLanguage('');
    } catch (e) {
      console.error('Failed to open AI document:', e);
      alert(t('errors.openFailed', 'Failed to open document.'));
    }
  }, [t]);

  // ──────────────────────────────────────────────────────────
  // Company resolution
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    const resolveCompany = async () => {
      try {
        if (!currentUser) return;
        const ownerSnap = await get(dbRef(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) { setCompanyUID(currentUser.uid); return; }
        const collabSnap = await get(dbRef(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          const c = collabSnap.val();
          if (c?.companyId) setCompanyUID(c.companyId);
        }
      } catch (e) { console.error('Company resolution error:', e); }
    };
    resolveCompany();
  }, [currentUser]);

  // Library subscription
  useEffect(() => {
    if (!companyUID) return;
    const r = dbRef(database, `companies/${companyUID}/contracts/docs`);
    return onValue(r, (snap) => {
      if (!snap.exists()) { setDocs([]); return; }
      const data = snap.val();
      const list = Object.entries(data).map(([id, v]) => ({ id, ...(v || {}) }));
      list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      setDocs(list);
    });
  }, [companyUID]);

  // Deep-link: load a saved AI doc into the editor
  useEffect(() => {
    const id = searchParams.get('aiDocId');
    if (!id || !companyUID) return;
    (async () => {
      try {
        const r = dbRef(database, `companies/${companyUID}/contracts/docs/${id}`);
        const snap = await get(r);
        if (!snap.exists()) return;
        const v = snap.val() || {};
        // Prefer stored Markdown; fall back to HTML path for legacy docs
        const content = v.markdownContent || (v.htmlPath
          ? await fetch(await getDownloadURL(stRef(storage, v.htmlPath))).then(r => r.text())
          : '');
        setDrafting(true);
        setMessages([]);
        setStatusText(t('ai.status.loaded', 'Loaded saved document'));
        setMasterContent(content);
        setActiveDoc({ id, title: v.title || '', tags: Array.isArray(v.tags) ? v.tags : [], type: v.type || 'ai' });
        setDocumentJurisdiction(v.country || v.jurisdiction || '');
        setDocumentLanguage(normalizeDocumentLanguage(v.language) || (containsArabicText(content) ? 'ar' : ''));
      } catch (e) { console.error('Failed to load AI doc:', e); }
    })();
  }, [searchParams, companyUID, t]);

  // ──────────────────────────────────────────────────────────
  // Generate (initial) — pure streaming, drafting state already set by caller
  // ──────────────────────────────────────────────────────────
  const handleGenerateDocument = async (promptText, context = {}) => {
    const generationJurisdiction = String(context.country || selectedDocumentJurisdiction || '').trim();
    const generationLanguage = context.language || selectedDocumentLanguage || '';
    if (!generationJurisdiction || !generationLanguage) {
      setStatusText(t('ai.status.missingJurisdictionLanguage', 'Please choose a jurisdiction and document language first.'));
      setClarifyStep('ready');
      return;
    }

    setStatusText(t('ai.status.researching', 'Browsing current legal resources…'));
    setIsGenerating(true);
    setMasterContent('');
    setActiveDoc(null);

    try {
      const controller = new AbortController();
      streamAbortRef.current = controller;
      const resp = await fetch(`${BACKEND_URL}/contracts/ai/generate-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptText,
          mode: 'initial',
          language: generationLanguage,
          country: generationJurisdiction,
          stream: true,
          model: CONTRACTS_MODEL,
          browse_resources: true,
          require_browsing: true,
          use_rag: true,
          kb: 'legal-docs',
        }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error('Failed to start AI stream');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let fullDoc = '';
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          if (!fullDoc) setStatusText(t('ai.status.generating', 'Generating document…'));
          fullDoc += chunk;
          await revealStreamChunk(chunk, part => setMasterContent(prev => prev + part));
        }
      }
      setStatusText(t('ai.status.generated', 'Document generated'));
      streamChatResponse(promptText, 'initial', fullDoc.slice(0, 600), {
        language: generationLanguage,
        country: generationJurisdiction,
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('AI stream error:', e);
        setStatusText(t('ai.status.failed', 'Failed to generate document'));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // ──────────────────────────────────────────────────────────
  // Clarification flow — enters drafting view, shows questions in chat pane
  // ──────────────────────────────────────────────────────────
  const handleAISubmit = async () => {
    if (!aiPrompt.trim()) return;
    if (!companyUID) { alert(t('errors.companyLoading', 'Please wait for company to load.')); return; }

    const promptText = aiPrompt.trim();
    const intakeLanguage = containsArabicText(promptText) ? 'ar' : interfaceLanguage;
    const intakeCopy = getClarifyCopy(intakeLanguage) || {};
    const intakeMandatoryQuestions = [
      {
        id: 'jurisdiction',
        type: 'text',
        required: true,
        question: intakeCopy.jurisdictionQuestion || t('ai.clarify.jurisdictionQuestion', 'Which country or jurisdiction should this document be adapted to?'),
        placeholder: intakeCopy.jurisdictionPlaceholder || t('ai.clarify.jurisdictionPlaceholder', 'Example: Lebanon, UAE, Germany, Ontario, DIFC'),
      },
      {
        id: 'document_language',
        type: 'choice',
        required: true,
        question: intakeCopy.languageQuestion || t('ai.clarify.languageQuestion', 'Generate this document in English or Arabic?'),
        options: [
          { label: intakeCopy.languageEnglish || t('ai.clarify.languageEnglish', 'English'), value: 'en' },
          { label: intakeCopy.languageArabic || t('ai.clarify.languageArabic', 'Arabic'), value: 'ar' },
        ],
      },
    ];

    // Enter drafting view immediately so questions appear in the chat pane
    setDrafting(true);
    setMessages([{ id: Date.now(), role: 'user', text: promptText }]);
    setMasterContent('');
    setActiveDoc(null);
    setStatusText(t('ai.clarify.loading', 'Analyzing your request…'));
    setClarifyStep('loading');
    setClarifyQuestions([]);
    setClarifyAnswers({});
    setClarifyLanguage(intakeLanguage);
    setDocumentJurisdiction('');
    setDocumentLanguage('');

    try {
      const resp = await fetch(`${BACKEND_URL}/contracts/ai/clarify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: promptText, language: intakeLanguage, country: '', model: CONTRACTS_MODEL }),
      });
      if (!resp.ok) throw new Error('Clarify fetch failed');
      const data = await resp.json();
      const aiQuestions = Array.isArray(data.questions) ? data.questions : [];
      const mandatoryIds = new Set(intakeMandatoryQuestions.map(q => q.id));
      setClarifyQuestions([
        ...intakeMandatoryQuestions,
        ...aiQuestions.filter(q => (
          q?.id
          && !mandatoryIds.has(q.id)
          && !isDuplicateJurisdictionQuestion(q)
        )),
      ]);
      setClarifyStep('ready');
    } catch (e) {
      console.warn('Clarification failed, using required intake questions only:', e);
      setClarifyQuestions(intakeMandatoryQuestions);
      setClarifyStep('ready');
    }
  };

  // Called when user clicks "Generate Document" after answering clarification questions
  const handleGenerateWithAnswers = () => {
    if (!requiredClarifyAnswersComplete) {
      setStatusText(t('ai.status.missingJurisdictionLanguage', 'Please choose a jurisdiction and document language first.'));
      return;
    }
    const jurisdiction = String(clarifyAnswers.jurisdiction || '').trim();
    const outputLanguage = clarifyAnswers.document_language || '';
    const answeredParts = clarifyQuestions
      .filter(q => clarifyAnswers[q.id])
      .filter(q => q.id !== 'jurisdiction' && q.id !== 'document_language')
      .map(q => `${q.question}: ${clarifyAnswers[q.id]}`);
    const enrichedPrompt = answeredParts.length > 0
      ? `${aiPrompt}\n\nAdditional context:\n${answeredParts.join('\n')}`
      : aiPrompt;
    setDocumentJurisdiction(jurisdiction);
    setDocumentLanguage(outputLanguage);
    setClarifyStep('idle');
    setClarifyQuestions([]);
    setClarifyAnswers({});
    setClarifyLanguage('');
    handleGenerateDocument(enrichedPrompt, {
      country: jurisdiction,
      language: outputLanguage,
    });
  };

  const handleClarifyAnswerChange = (question, value) => {
    setClarifyAnswers(prev => ({ ...prev, [question.id]: value }));
    if (question.id === 'jurisdiction') setDocumentJurisdiction(value);
    if (question.id === 'document_language') setDocumentLanguage(value);
  };

  // ──────────────────────────────────────────────────────────
  // Edit — patch first, full re-stream as fallback
  // ──────────────────────────────────────────────────────────
  const handleEditSubmit = async () => {
    const text = editText.trim();
    if (!text || isGenerating || isApplyingEdit) return;
    const selectedContext = selectedExcerpt;
    const hasSelectedContext = Boolean(selectedContext);
    const currentContent = editorContentRef.current?.innerText || masterContent || '';
    const originalEditorHTML = editorContentRef.current?.innerHTML || '';

    setMessages(prev => [...prev, { id: Date.now(), role: 'user', text }]);
    setStatusText(t('ai.status.updating', 'Updating…'));
    setIsApplyingEdit(true);
    setEditText('');

    // 1. Try targeted patch — fast, scrolls to changed section
    try {
      const patchResp = await fetch(`${BACKEND_URL}/contracts/ai/patch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: text,
          current_content: currentContent,
          current_html: getHTMLContent(),
          selected_text: selectedContext,
          section_index: buildEditorSectionIndex(),
          research_policy: 'intent_based',
          language: selectedDocumentLanguage || interfaceLanguage,
          country: selectedDocumentJurisdiction,
          model: CONTRACTS_MODEL,
        }),
      });
      if (patchResp.ok) {
        const result = await patchResp.json();

        const operations = Array.isArray(result.operations) ? result.operations : [];
        if (!operations.length && result.selected_replacement) {
          operations.push({ type: 'replace_selection', replacement: result.selected_replacement });
        }
        if (!operations.length && result.section_heading && result.replacement) {
          operations.push({ type: 'replace_section', section_heading: result.section_heading, replacement: result.replacement });
        }
        if (!operations.length && result.full_replacement && result.document) {
          operations.push({ type: 'redraft_document', document: result.document });
        }

        if (operations.length) {
          let changed = false;
          let scrollTarget = null;
          for (const operation of operations) {
            if (operation.type === 'redraft_document' && !isExplicitRedraftRequest(text)) {
              throw new Error('AI returned a redraft operation for a non-redraft prompt');
            }
            const target = await applyEditOperation(operation, text);
            if (!target) {
              throw new Error(`Failed to apply edit operation: ${operation.type || 'unknown'}`);
            }
            changed = true;
            if (!scrollTarget && target?.scrollIntoView) scrollTarget = target;
          }

          if (!changed) throw new Error('No edit operations were applied');
          syncEditorState();
          scrollTarget?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          setStatusText(t('ai.status.updated', 'Document updated'));
          setIsApplyingEdit(false);
          streamChatResponse(text, 'patch', result.summary || text, {
            language: selectedDocumentLanguage || interfaceLanguage,
            country: selectedDocumentJurisdiction,
          });
          return;
        }
      }
      if (hasSelectedContext) {
        throw new Error('Selected text patch request failed');
      }
    } catch (e) {
      if (hasSelectedContext) {
        console.warn('Selected text patch failed:', e);
        if (originalEditorHTML && editorContentRef.current) editorContentRef.current.innerHTML = originalEditorHTML;
        setStatusText(t('ai.status.updateFailed', 'Update failed'));
        setIsApplyingEdit(false);
        return;
      }
      if (originalEditorHTML && editorContentRef.current) editorContentRef.current.innerHTML = originalEditorHTML;
      console.warn('Patch operation failed:', e);
    }

    setStatusText(t('ai.status.updateFailed', 'Update failed'));
    setIsApplyingEdit(false);
  };

  // ──────────────────────────────────────────────────────────
  // Save / discard
  // ──────────────────────────────────────────────────────────
  const handleSave = () => {
    const html = getHTMLContent();
    if (!html.trim()) { alert(t('errors.emptyDocument', 'Document is empty')); return; }
    if (!companyUID || !currentUser) { alert(t('errors.notReady', 'Not ready')); return; }
    if (activeDoc?.id) {
      updateExistingDocument();
      return;
    }
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const h = temp.querySelector('h1,h2,h3');
    setSaveTitle(h ? h.textContent.trim() : t('save.defaultTitle', 'Untitled AI Document'));
    setSaveTags([]);
    setSaveTagInput('');
    setShowSaveModal(true);
  };

  const handleDeleteDraft = () => {
    if (!window.confirm(t('ai.discardConfirm', 'Discard current draft?'))) return;
    setDrafting(false);
    setMasterContent('');
    setMessages([]);
    setChatStreamText('');
    setIsChatResponding(false);
    setStatusText('');
    setAiPrompt('');
    setActiveDoc(null);
    setClarifyStep('idle');
    setClarifyQuestions([]);
    setClarifyAnswers({});
    setClarifyLanguage('');
    setDocumentJurisdiction('');
    setDocumentLanguage('');
    if (streamAbortRef.current) streamAbortRef.current.abort();
  };

  const finishAfterSave = useCallback(() => {
    setShowSaveModal(false);
    setDrafting(false);
    setMasterContent('');
    setMessages([]);
    setAiPrompt('');
    setActiveDoc(null);
    if (editorContentRef.current) editorContentRef.current.innerHTML = '';
  }, []);

  const updateExistingDocument = async () => {
    if (!activeDoc?.id) return;
    try {
      setSaving(true);
      const html = getHTMLContent();
      const cleanTitle = activeDoc.title || t('save.defaultTitle', 'Untitled AI Document');
      const pdfPath = `companies/${companyUID}/contracts/docs/${activeDoc.id}.pdf`;
      let pdfURL = '';
      try {
        const pdfBlob = await createPDFBlob(html, cleanTitle);
        await uploadBytes(stRef(storage, pdfPath), pdfBlob, { contentType: 'application/pdf' });
        pdfURL = await getDownloadURL(stRef(storage, pdfPath));
      } catch (e) {
        console.error('PDF generation failed:', e);
        alert(t('errors.pdfGenerationFailed', 'Warning: PDF generation failed. Only the text version will be saved.'));
      }

      await set(dbRef(database, `companies/${companyUID}/contracts/docs/${activeDoc.id}`), {
        title: cleanTitle,
        type: 'ai',
        HTMLContract: html,
        markdownContent: editorContentRef.current?.innerText || masterContent,
        country: selectedDocumentJurisdiction,
        language: selectedDocumentLanguage,
        downloadURL: pdfURL || null,
        pdfPath,
        tags: activeDoc.tags || [],
        updatedAt: Date.now(),
      });

      finishAfterSave();
      alert(t('messages.updated', 'Document updated successfully!'));
    } catch (e) {
      console.error('Update failed:', e);
      alert(t('errors.saveFailed', 'Failed to save document'));
    } finally {
      setSaving(false);
    }
  };

  const saveDocument = async () => {
    try {
      setSaving(true);
      const html = getHTMLContent();
      const cleanTitle = saveTitle.trim();
      if (!cleanTitle) { alert(t('errors.missingTitle', 'Please enter a title')); setSaving(false); return; }

      const newRef = push(dbRef(database, `companies/${companyUID}/contracts/docs`));
      const docId = newRef.key;
      const pdfPath = `companies/${companyUID}/contracts/docs/${docId}.pdf`;

      let pdfURL = '';
      try {
        const pdfBlob = await createPDFBlob(html, cleanTitle);
        await uploadBytes(stRef(storage, pdfPath), pdfBlob, { contentType: 'application/pdf' });
        pdfURL = await getDownloadURL(stRef(storage, pdfPath));
      } catch (e) {
        console.error('PDF generation failed:', e);
        alert(t('errors.pdfGenerationFailed', 'Warning: PDF generation failed. Only the text version will be saved.'));
      }

      await set(newRef, {
        title: cleanTitle,
        type: 'ai',
        HTMLContract: html,
        markdownContent: editorContentRef.current?.innerText || masterContent,
        country: selectedDocumentJurisdiction,
        language: selectedDocumentLanguage,
        downloadURL: pdfURL || null,
        pdfPath,
        tags: saveTags,
        updatedAt: Date.now(),
      });

      finishAfterSave();
      alert(t('messages.saved', 'Document saved successfully!'));
    } catch (e) {
      console.error('Save failed:', e);
      alert(t('errors.saveFailed', 'Failed to save document'));
    } finally {
      setSaving(false);
    }
  };

  const saveUpload = async () => {
    try {
      if (!companyUID || !currentUser) return;
      if (!uploadFile) { alert(t('errors.chooseFile', 'Please choose a file')); return; }
      const cleanTitle = (uploadTitle || uploadFile.name.replace(/\.[^.]+$/, '')).trim();
      if (!cleanTitle) { alert(t('errors.missingTitle', 'Please enter a title')); return; }
      const path = `companies/${companyUID}/contracts/docs/${Date.now()}_${uploadFile.name}`;
      const storageRef = stRef(storage, path);
      await uploadBytes(storageRef, uploadFile);
      const url = await getDownloadURL(storageRef);
      const newRef = push(dbRef(database, `companies/${companyUID}/contracts/docs`));
      await set(newRef, { title: cleanTitle, tags: uploadTags, storagePath: path, downloadURL: url, type: 'uploaded' });
      setShowUploadModal(false);
      setUploadTitle(''); setUploadTags([]); setUploadTagInput(''); setUploadFile(null);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(t('errors.uploadFailed', 'Upload failed. Please try again.'));
    }
  };

  // ──────────────────────────────────────────────────────────
  // Library helpers
  // ──────────────────────────────────────────────────────────
  const uniqueTags = useMemo(() => {
    const s = new Set();
    docs.forEach(d => (Array.isArray(d.tags) ? d.tags : []).forEach(tag => tag && s.add(tag)));
    uploadTags.forEach(tag => tag && s.add(tag));
    saveTags.forEach(tag => tag && s.add(tag));
    sessionTags.forEach(tag => tag && s.add(tag));
    return Array.from(s);
  }, [docs, uploadTags, saveTags, sessionTags]);

  const filteredDocs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return docs.filter(d => {
      const title = (d.title || '').toLowerCase();
      const tags = (d.tags || []).map(x => x.toLowerCase());
      const matchesQuery = !q || title.includes(q) || tags.some(tag => tag.includes(q));
      const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => (d.tags || []).includes(tag));
      return matchesQuery && matchesTags;
    });
  }, [docs, searchQuery, selectedTags]);

  const onClickUpload = () => setShowUploadModal(true);
  const handleFilePicked = (e) => { const f = e.target.files?.[0]; if (f) setUploadFile(f); };
  const toggleTagFilter = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const addUploadTag = () => {
    const tag = uploadTagInput.trim();
    if (!tag) return;
    if (!uploadTags.includes(tag)) setUploadTags(prev => [...prev, tag]);
    setSessionTags(prev => prev.some(t => t.toLowerCase() === tag.toLowerCase()) ? prev : [...prev, tag]);
    setUploadTagInput('');
  };
  const removeUploadTag = (tag) => setUploadTags(prev => prev.filter(x => x !== tag));
  const addSaveTag = () => {
    const tag = saveTagInput.trim();
    if (!tag) return;
    if (!saveTags.includes(tag)) setSaveTags(prev => [...prev, tag]);
    setSessionTags(prev => prev.some(t => t.toLowerCase() === tag.toLowerCase()) ? prev : [...prev, tag]);
    setSaveTagInput('');
  };
  const removeSaveTag = (tag) => setSaveTags(prev => prev.filter(x => x !== tag));

  // ──────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div
        className={`contracts-body${drafting ? ' contracts-body-drafting' : ''}${isRTL ? ' rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          '--sidebar-w': sidebarCollapsed ? '60px' : '240px',
          '--body-right-pad': sidebarCollapsed ? '100px' : '40px',
        }}
      >
        <FeatureGate showBanner={false}>
          {!drafting && <h2 className="contracts-header">{t('title', 'Contracts & Docs')}</h2>}

          {/* ── LIBRARY VIEW ── */}
          {!drafting && (
            <div className="contracts-sections">
              {canUseAIContracts ? (
                <div className="ai-generator">
                  <div className="ai-tag">
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#568ded', width: '19px', height: '18px' }} />
                    <span>{t('ai.powered', 'Powered by AI')}</span>
                  </div>
                  <h3 className="ai-title">{t('ai.heroTitle', 'Create legal docs in seconds')}</h3>
                  <p className="ai-desc">
                    {t('ai.heroDesc', 'Draft Employment Contracts, NDAs, IPs, and other Legal documents for your new and current hires with our AI Legal Agent.')}
                  </p>
                  <div className="doc-icon">
                    <div className="doc-line-set" style={{ marginTop: '6px' }}>
                      <div className="doc-line long" /><div className="doc-line medium" />
                    </div>
                    <div className="doc-line-set" style={{ marginTop: '10px' }}>
                      <div className="doc-line long" /><div className="doc-line short" />
                    </div>
                  </div>
                  <div
                    className={`doc-textarea-wrapper${aiPromptPulse ? ' prompt-pulse' : ''}`}
                    onAnimationEnd={() => setAiPromptPulse(false)}
                  >
                    <textarea
                      ref={aiPromptRef}
                      className="doc-textarea"
                      placeholder={t('ai.promptPlaceholder', 'What legal document would you like to draft?')}
                      value={aiPrompt}
                      onChange={e => setAiPrompt(e.target.value)}
                      rows={3}
                    />
                    <button className="doc-submit-btn" onClick={handleAISubmit} title={t('ai.submit', 'Submit')}>
                      <i className="fa-solid fa-paper-plane" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="ai-generator locked-feature-overlay"
                  onClick={() => setShowUpgradeModal(true)}
                  style={{ position: 'relative', cursor: 'pointer', opacity: 0.7 }}
                >
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', zIndex: 10,
                    background: 'var(--app-surface, rgba(3,17,40,0.95))', padding: '24px 32px',
                    borderRadius: '12px', border: '2px solid #3498db', textAlign: 'center',
                  }}>
                    <i className="fa-solid fa-lock" style={{ fontSize: '32px', color: 'var(--app-tone-3498db, #3498db)', marginBottom: '10px' }} />
                    <h3 style={{ color: 'var(--app-text, white)', margin: '10px 0 8px 0', fontSize: '18px', fontWeight: 600 }}>{t('ai.lockedTitle', 'AI Contract Generation')}</h3>
                    <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', margin: '0 0 15px 0', fontSize: '14px' }}>
                      {t('ai.lockedMessage', 'Upgrade to Medium Teams or higher')}
                    </p>
                    <button className="post-job-cta" style={{ width: '100%', justifyContent: 'center' }}
                      onClick={e => { e.stopPropagation(); setShowUpgradeModal(true); }}>
                      {t('ai.viewPlans', 'View Plans')}
                    </button>
                  </div>
                  <div style={{ filter: 'blur(3px)', pointerEvents: 'none' }}>
                    <div className="ai-tag">
                      <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#568ded', width: '19px', height: '18px' }} />
                      <span>{t('ai.powered', 'Powered by AI')}</span>
                    </div>
                    <h3 className="ai-title">{t('ai.heroTitle', 'Create legal docs in seconds')}</h3>
                    <p className="ai-desc">{t('ai.heroDesc', 'Draft Employment Contracts, NDAs, IPs, and other Legal documents for your new and current hires with our AI Legal Agent.')}</p>
                    <div className="doc-icon">
                      <div className="doc-line-set" style={{ marginTop: '6px' }}>
                        <div className="doc-line long" /><div className="doc-line medium" />
                      </div>
                      <div className="doc-line-set" style={{ marginTop: '10px' }}>
                        <div className="doc-line long" /><div className="doc-line short" />
                      </div>
                    </div>
                    <div className="doc-textarea-wrapper">
                      <textarea className="doc-textarea" placeholder={t('ai.promptPlaceholder', 'What legal document would you like to draft?')} disabled rows={3} />
                      <button className="doc-submit-btn" disabled title={t('ai.lockedTitle', 'AI Contract Generation')}>
                        <i className="fa-solid fa-lock" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="my-docs-section">
                <div className="my-docs-title">{t('library.title', 'My docs')}</div>
                <div className="my-docs-subtext">{t('library.subtitle', 'This is your library of contract templates. Use these reusable templates to later add specific details of a certain candidate or employee.')}</div>
                <div className="docs-toolbar">
                  <button className="post-job-cta" onClick={onClickUpload}>
                    <i className="fa-solid fa-circle-plus" /><span>{t('library.upload', 'Upload')}</span>
                  </button>
                  <div className="team-search">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input type="text" placeholder={t('library.searchPlaceholder', 'Search docs...')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                  </div>
                  <div className="tag-multiselect">
                    <button className="tag-select-btn" onClick={() => setShowFilterDropdown(v => !v)}>
                      <i className="fa-solid fa-tags" style={{ color: '#568ded' }} />
                      <span>{t('library.filterByTags', 'Filter by Tags')}{selectedTags.length ? ` (${selectedTags.length})` : ''}</span>
                    </button>
                    {showFilterDropdown && (
                      <div className="tag-dropdown" onMouseLeave={() => setShowFilterDropdown(false)}>
                        {uniqueTags.length === 0 ? (
                          <div style={{ padding: '6px 4px', color: '#666' }}>{t('library.noTags', 'No tags yet')}</div>
                        ) : uniqueTags.map(tag => (
                          <label key={tag} className="tag-option">
                            <input type="checkbox" checked={selectedTags.includes(tag)} onChange={() => toggleTagFilter(tag)} />
                            <span>{tag}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {selectedTags.length > 0 && (
                  <div className="tag-filter" style={{ marginBottom: '12px' }}>
                    {selectedTags.map(tag => (
                      <span key={tag} className="tag-chip active" title={t('library.selectedTag', { tag, defaultValue: `Selected: ${tag}` })}>
                        {tag}
                        <button type="button" className="tag-chip-remove" aria-label={t('library.removeTag', { tag, defaultValue: `Remove ${tag}` })}
                          onClick={e => { e.stopPropagation(); toggleTagFilter(tag); }}>
                          <i className="fa-solid fa-times" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="docs-grid">
                  {filteredDocs.map(doc => (
                    <div key={doc.id} className="doc-card"
                      onClick={() => {
                        if (doc.type === 'ai') {
                          openAIDocument(doc);
                          return;
                        }
                        if (doc.downloadURL) window.open(doc.downloadURL, '_blank', 'noopener,noreferrer');
                      }}
	                      title={doc.type === 'ai' ? t('library.openAiDoc', 'Open document') : t('library.openPdf', 'Open PDF')}
	                    >
	                      <div className="doc-card-header">
	                        <div className="doc-card-actions">
	                          <button
	                            type="button"
	                            className="doc-card-action-btn"
	                            title={t('library.previewPdf', 'Preview PDF')}
	                            disabled={!doc.downloadURL}
	                            onClick={e => {
	                              e.stopPropagation();
	                              if (doc.downloadURL) window.open(doc.downloadURL, '_blank', 'noopener,noreferrer');
	                            }}
	                          >
	                            <i className="fa-regular fa-eye" />
	                          </button>
	                          <button
	                            type="button"
	                            className="doc-card-action-btn"
	                            title={t('library.downloadPdf', 'Download PDF')}
	                            disabled={!doc.downloadURL}
	                            onClick={e => {
	                              e.stopPropagation();
	                              if (!doc.downloadURL) return;
	                              const link = document.createElement('a');
	                              link.href = doc.downloadURL;
	                              link.download = `${doc.title || 'document'}.pdf`;
	                              link.target = '_blank';
	                              link.rel = 'noopener noreferrer';
	                              document.body.appendChild(link);
	                              link.click();
	                              document.body.removeChild(link);
	                            }}
	                          >
	                            <i className="fa-solid fa-download" />
	                          </button>
	                        </div>
	                        <button className="doc-delete-btn" title={t('library.delete', 'Delete')}
	                          onClick={async e => {
	                            e.stopPropagation();
	                            if (!window.confirm(t('library.confirmDelete', 'Delete this template? This cannot be undone.'))) return;
	                            try {
	                              if (doc.storagePath) { try { await deleteObject(stRef(storage, doc.storagePath)); } catch { } }
	                              if (doc.pdfPath) { try { await deleteObject(stRef(storage, doc.pdfPath)); } catch { } }
	                              await remove(dbRef(database, `companies/${companyUID}/contracts/docs/${doc.id}`));
	                            } catch (err) { console.error('Delete failed:', err); alert(t('errors.deleteFailed', 'Failed to delete document.')); }
	                          }}>
                          <i className="fa-solid fa-circle-xmark" />
                        </button>
                      </div>
                      <div className={`doc-preview${doc.downloadURL ? ' embed' : ''}`}>
                        {doc.downloadURL
                          ? <embed src={doc.downloadURL} type="application/pdf" className="pdf-embed" />
                          : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.7 }}>{t('library.noPreview', 'No preview')}</div>
                        }
                      </div>
                      <div className="doc-title">{doc.title || t('library.untitled', 'Untitled')}</div>
                      <div className="doc-tags">
                        {(doc.tags || []).map(tag => <span key={tag} className="doc-tag">{tag}</span>)}
                      </div>
                    </div>
                  ))}
                  {filteredDocs.length === 0 && (
                    selectedTags.length === 0 && searchQuery.trim() === '' ? (
                      <div className="docs-empty-state" style={{ gridColumn: '1 / -1' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '4px' }}>{t('library.emptyTitle', 'Your library is empty')}</div>
                          <div style={{ fontSize: '13px', opacity: 0.6 }}>{t('library.emptySubtitle', 'Use the AI agent above to generate your first contract, or upload an existing document.')}</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ opacity: 0.7 }}>{t('library.noMatches', 'No documents match your filters.')}</div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── DRAFTING VIEW ── */}
          {drafting && (
            <div className="ai-shell">
              <div className="ai-drafting-container">

                {/* LEFT — Document editor */}
                <div className="editor-pane">
                  <div className="editor-toolbar">
                    <button
                      onClick={() => execFormat('bold')}
                      title={t('editor.bold', 'Bold')}
                      disabled={isGenerating}
                      className={formatState.bold ? 'toolbar-active' : ''}
                    ><i className="fa-solid fa-bold" /></button>
                    <button
                      onClick={() => execFormat('italic')}
                      title={t('editor.italic', 'Italic')}
                      disabled={isGenerating}
                      className={formatState.italic ? 'toolbar-active' : ''}
                    ><i className="fa-solid fa-italic" /></button>
                    <button
                      onClick={() => execFormat('underline')}
                      title={t('editor.underline', 'Underline')}
                      disabled={isGenerating}
                      className={formatState.underline ? 'toolbar-active' : ''}
                    ><i className="fa-solid fa-underline" /></button>
                    <button
                      onClick={() => execFormat('insertUnorderedList')}
                      title={t('editor.bulletList', 'Bulleted list')}
                      disabled={isGenerating}
                    ><i className="fa-solid fa-list-ul" /></button>
                    <button
                      onClick={() => execFormat('insertOrderedList')}
                      title={t('editor.numberList', 'Numbered list')}
                      disabled={isGenerating}
                    ><i className="fa-solid fa-list-ol" /></button>
                  </div>

                  <div className="editor-scroll" ref={editorScrollRef}>
                    {(isGenerating || clarifyStep === 'loading') ? (
                      <div
                        className={`doc-page legal-doc generating ${documentDirectionClass}`}
                        dir={documentDirection}
                        lang={selectedDocumentLanguage || undefined}
                      >
                        {masterContent ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {masterContent}
                          </ReactMarkdown>
                        ) : (
                          <div className="generation-research-state">
                            <i className="fa-solid fa-magnifying-glass" />
                            <p>{statusText || t('ai.status.researching', 'Browsing current legal resources…')}</p>
                            <div className="research-lines" aria-hidden="true">
                              <span />
                              <span />
                              <span />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
	                      <div
	                        ref={editorContentRef}
	                        className={`doc-page legal-doc wysiwyg-editor ${documentDirectionClass}`}
	                        dir={documentDirection}
	                        lang={selectedDocumentLanguage || undefined}
	                        contentEditable
                        suppressContentEditableWarning
                        spellCheck
                        onMouseUp={updateSelectedExcerpt}
                        onKeyUp={updateSelectedExcerpt}
                        aria-label={t('editor.ariaLabel', 'Document editor')}
                      />
                    )}
                  </div>

                  <div className="editor-footer">
                    <button className="btn btn-delete" onClick={handleDeleteDraft}>
                      <i className="fa-regular fa-trash-can" /> {t('editor.discard', 'Discard')}
                    </button>
                    <button className="btn btn-save" onClick={handleSave} disabled={isGenerating || !masterContent}>
                      <i className="fa-regular fa-floppy-disk" /> {t('editor.save', 'Save')}
                    </button>
                  </div>
                </div>

                {/* RIGHT — Chat / edit pane */}
                <div className="chat-pane">
                  <div className="chat-messages" ref={chatMessagesRef}>
                    {messages.map((m, idx) => (
                      <div key={m.id}>
                        {m.role === 'ai' ? (
                          <div className="chat-ai-response">
                            <div className="chat-ai-header">
                              <i className="fa-solid fa-wand-magic-sparkles chat-ai-icon" />
                              <span className="chat-ai-label">{t('ai.agentLabel', 'AI Legal Agent')}</span>
                            </div>
                            <div className="chat-ai-body">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="chat-line">{m.text}</div>
                            {/* Status pill between this user message and its AI reply,
                                only while the doc is actively generating */}
                            {idx === messages.length - 1 && isGenerating && (
                              <div className="status-message">
                                <span className="status-pill">
                                  <i className="fa-solid fa-wand-magic-sparkles" />
                                  {statusText || t('ai.status.generating', 'Generating document…')}
                                  <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineStart: 8 }} />
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                    {/* Clarification questions — shown as agent message */}
                    {clarifyStep === 'loading' && (
                      <div className="chat-ai-response">
                        <div className="chat-ai-header">
                          <i className="fa-solid fa-wand-magic-sparkles chat-ai-icon" />
                          <span className="chat-ai-label">{t('ai.agentLabel', 'AI Legal Agent')}</span>
                          <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineStart: 'auto', color: '#568ded', fontSize: 12 }} />
                        </div>
                        <div className="chat-ai-body">
                          <p style={{ margin: 0, fontStyle: 'italic', opacity: 0.8 }}>
                            {t('ai.clarify.loading', 'Analyzing your request…')}
                          </p>
                        </div>
                      </div>
                    )}
                    {clarifyStep === 'ready' && clarifyQuestions.length > 0 && (
                      <div className="chat-ai-response">
                        <div className="chat-ai-header">
                          <i className="fa-solid fa-wand-magic-sparkles chat-ai-icon" />
                          <span className="chat-ai-label">{t('ai.agentLabel', 'AI Legal Agent')}</span>
                        </div>
                        <div className="chat-ai-body chat-clarify-body">
                          <p className="clarify-intro">{clarifyCopy.header || t('ai.clarify.header', 'A few details to tailor your document:')}</p>
                          {clarifyQuestions.map(q => (
                            <div key={q.id} className="clarify-question">
                              <div className="clarify-question-text">{q.question}</div>
                              {q.type === 'text' ? (
                                <input
                                  className="clarify-text-input"
                                  type="text"
                                  value={clarifyAnswers[q.id] || ''}
                                  placeholder={q.placeholder || ''}
                                  onChange={e => handleClarifyAnswerChange(q, e.target.value)}
                                />
                              ) : (
                                <div className="clarify-options">
                                  {(q.options || []).map(opt => {
                                    const value = getClarifyOptionValue(opt);
                                    const label = getClarifyOptionLabel(opt);
                                    return (
                                      <button
                                        key={value}
                                        className={`clarify-option-chip${clarifyAnswers[q.id] === value ? ' selected' : ''}`}
                                        onClick={() => handleClarifyAnswerChange(q, value)}
                                      >
                                        {uiText(label)}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                          <button
                            className="clarify-generate-btn"
                            onClick={handleGenerateWithAnswers}
                            disabled={!requiredClarifyAnswersComplete}
                          >
                            <i className="fa-solid fa-wand-magic-sparkles" />
                            <span>{clarifyCopy.generate || t('ai.clarify.generate', 'Generate Document')}</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Live-streaming AI chat response */}
                    {chatStreamText && (
                      <div className="chat-ai-response streaming">
                        <div className="chat-ai-header">
                          <i className="fa-solid fa-wand-magic-sparkles chat-ai-icon" />
                          <span className="chat-ai-label">{t('ai.agentLabel', 'AI Legal Agent')}</span>
                          <i className="fa-solid fa-spinner fa-spin" style={{ marginInlineStart: 'auto', color: '#568ded', fontSize: 12 }} />
                        </div>
                        <div className="chat-ai-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{chatStreamText}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="edit-box">
                    {selectedExcerpt && clarifyStep === 'idle' && !isGenerating && (
                      <div className="selected-context-pill" title={selectedExcerpt}>
                        <span>{t('editor.highlightedContext', 'Highlighted context')}: "{selectedContextLabel}"</span>
                        <button
                          type="button"
                          onClick={clearSelectedContext}
                          aria-label={t('editor.clearHighlightedContext', 'Clear highlighted context')}
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    )}
                    <textarea
                      className="edit-textarea"
                      placeholder={t('ai.editPlaceholder', 'Request edits (e.g., change probation to 3 months, add confidentiality clause)')}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleEditSubmit(); } }}
                      disabled={isGenerating || isApplyingEdit || clarifyStep !== 'idle'}
                    />
                    <button className="edit-send-btn" onClick={handleEditSubmit} disabled={isGenerating || isApplyingEdit || clarifyStep !== 'idle'}>
                      <i className={`fa-solid ${isApplyingEdit ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showUpgradeModal && (
            <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)}
              requiredFeature="aiContracts" featureDisplayName={t('ai.featureName', 'AI Contract Generation')} />
          )}
        </FeatureGate>
      </div>

      {/* ── Upload modal ── */}
      {showUploadModal && (
        <div className="cd-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="cd-modal-content" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2>{t('upload.title', 'Upload Document')}</h2>
              <button className="cd-close" onClick={() => setShowUploadModal(false)}>&times;</button>
            </div>
            <div className="cd-modal-body">
              <div className="cd-input">
                <input type="text" placeholder={t('upload.fields.title', 'Title')} value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} />
              </div>
              <div className="cd-input">
                <div className={`cd-tag-input-wrap${saveTagInput.trim() ? ' cd-tag-input-wrap-open' : ''}`}>
                  <input type="text" placeholder={t('upload.fields.addTags', 'Add tags…')} value={uploadTagInput}
                    onChange={e => setUploadTagInput(e.target.value)}
                    onFocus={() => setShowUploadTagDropdown(true)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addUploadTag(); setShowUploadTagDropdown(false); } }} />
                  <button className="btn-outline-brand" onClick={addUploadTag}>{t('upload.actions.add', 'Add')}</button>
                  {(showUploadTagDropdown || uploadTagInput.trim() !== '') && (
                    <div className="cd-tag-dropdown" onMouseDown={e => e.preventDefault()}>
                      {uniqueTags.filter(t => !uploadTags.includes(t)).filter(t => t.toLowerCase().includes(uploadTagInput.trim().toLowerCase()))
                        .map(tag => (
                          <div key={tag} className="cd-tag-item" onClick={() => { if (!uploadTags.includes(tag)) setUploadTags(p => [...p, tag]); setUploadTagInput(''); setShowUploadTagDropdown(false); }}>{tag}</div>
                        ))}
                      {uploadTagInput.trim() && !uniqueTags.some(t => t.toLowerCase() === uploadTagInput.trim().toLowerCase()) && (
                        <div className="cd-tag-item" onClick={() => { addUploadTag(); setShowUploadTagDropdown(false); }}>
                          {t('upload.actions.addTagValue', { value: uploadTagInput.trim(), defaultValue: `Add "${uploadTagInput.trim()}"` })}
                        </div>
                      )}
                      {uniqueTags.length === 0 && !uploadTagInput.trim() && (
                        <div className="cd-tag-item" style={{ color: '#666' }}>{t('upload.emptyTags', 'No existing tags')}</div>
                      )}
                    </div>
                  )}
                </div>
                {uploadTags.length > 0 && (
                  <div className="cd-tags" style={{ marginTop: '10px' }}>
                    {uploadTags.map(tag => (
                      <span key={tag} className="cd-tag">{tag}<button onClick={() => removeUploadTag(tag)} title={t('upload.actions.remove', 'Remove')}>&times;</button></span>
                    ))}
                  </div>
                )}
              </div>
              <div className="cd-input">
                <input type="file" accept="application/pdf" onChange={handleFilePicked} />
                {uploadFile && <div style={{ marginTop: '6px', color: 'var(--app-heading, #031128)' }}>{t('upload.selected', { name: uploadFile.name, defaultValue: `Selected: ${uploadFile.name}` })}</div>}
              </div>
            </div>
            <div className="cd-footer">
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>{t('common:buttons.cancel', 'Cancel')}</button>
              <button className="btn-primary" onClick={saveUpload}>{t('common:buttons.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Save modal ── */}
      {showSaveModal && (
        <div className="cd-modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="cd-modal-content cd-save-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <div>
                <h2>{t('save.title', 'Save Document')}</h2>
              </div>
              <button className="cd-close" onClick={() => setShowSaveModal(false)} aria-label={t('common:buttons.close', 'Close')}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="cd-modal-body">
              <div className="cd-input">
                <label htmlFor="save-document-title">{t('save.fields.titleLabel', 'Document name')}</label>
                <input id="save-document-title" type="text" placeholder={t('save.fields.title', 'Document title')} value={saveTitle} onChange={e => setSaveTitle(e.target.value)} />
              </div>
              <div className="cd-input">
                <label htmlFor="save-document-tags">{t('save.fields.tagsLabel', 'Tags')}</label>
                <div className={`cd-tag-input-wrap${saveTagInput.trim() ? ' cd-tag-input-wrap-open' : ''}`}>
                  <input id="save-document-tags" type="text" placeholder={t('save.fields.addTags', 'Add tags…')} value={saveTagInput}
                    onChange={e => setSaveTagInput(e.target.value)}
                    onFocus={() => setShowSaveTagDropdown(true)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSaveTag(); setShowSaveTagDropdown(false); } }} />
                  {saveTagInput.trim() !== '' && (
                    <div className="cd-tag-dropdown" onMouseDown={e => e.preventDefault()}>
                      {uniqueTags.filter(t => !saveTags.includes(t)).filter(t => t.toLowerCase().includes(saveTagInput.trim().toLowerCase()))
                        .map(tag => (
                          <div key={tag} className="cd-tag-item" onClick={() => { if (!saveTags.includes(tag)) setSaveTags(p => [...p, tag]); setSaveTagInput(''); setShowSaveTagDropdown(false); }}>{tag}</div>
                        ))}
                      {saveTagInput.trim() && !uniqueTags.some(t => t.toLowerCase() === saveTagInput.trim().toLowerCase()) && (
                        <div className="cd-tag-item cd-tag-add-item" onClick={() => { addSaveTag(); setShowSaveTagDropdown(false); }}>
                          <i className="fa-solid fa-plus" />
                          <span>{t('save.actions.addNewTag', 'Add new tag')}</span>
                          <strong>{saveTagInput.trim()}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {saveTags.length > 0 && (
                  <div className="cd-tags">
                    {saveTags.map(tag => (
                      <span key={tag} className="cd-tag">
                        <span>{tag}</span>
                        <button onClick={() => removeSaveTag(tag)} title={t('save.actions.remove', 'Remove')} aria-label={t('save.actions.remove', 'Remove')}>
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="cd-save-note">
                <i className="fa-regular fa-file-lines" />
                {t('save.description', 'This will save both an editable version and a PDF to your document library.')}
              </div>
            </div>
            <div className="cd-footer">
              <button className="btn-secondary" onClick={() => setShowSaveModal(false)}>{t('common:buttons.cancel', 'Cancel')}</button>
              <button className="btn-primary" onClick={saveDocument} disabled={saving}>
                {saving ? t('save.saving', 'Saving...') : t('save.submit', 'Save Document')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsDocs;
